"use client";
"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Input = void 0;
const react_1 = __importDefault(require("react"));
const foundations_1 = require("../../foundations");
const useId_1 = require("../../hooks/useId");
const OptionDropdown_1 = require("./OptionDropdown");
const Input = ({ label, value, name, type = "text", onChange, onBlur, onSelectOption, onClickClear, onEnter, disabled, required, readOnly, error, hint, success, autocomplete = "off", searchIcon, size = "medium", dropdownOptions = [], showNoResults, optionDisplayCount, noResultsMessage, sx }) => {
    var _a;
    const inputId = (0, useId_1.useId)();
    const [isFocused, setIsFocused] = react_1.default.useState(false);
    const clearButtonRef = react_1.default.useRef(null);
    const { inputRef, focusedOptionIdx, handleKeyDown, showDropdown, iconButtonRef, handleClickOption, ariaPropsForDropdown, handleCompositionStart, handleCompositionEnd } = (0, OptionDropdown_1.useOptionDropdown)({
        isSelect: type === "select",
        dropdownOptions,
        onSelectOption,
        isFocused,
        onEnter
    });
    const theme = (0, foundations_1.useTheme)();
    const shouldShowLabel = isFocused || !!value;
    const borderRadius = (_a = theme.radii) === null || _a === void 0 ? void 0 : _a.inputBorderRadius;
    const leftOffset = react_1.default.useMemo(() => {
        if (borderRadius && typeof borderRadius === "number" && borderRadius > 10) {
            return 6;
        }
        else {
            return 4;
        }
    }, [borderRadius]);
    const iconDisplay = react_1.default.useMemo(() => {
        return error ? (react_1.default.createElement(foundations_1.Icon, { icon: "info", size: "sm", sx: { color: "error01", display: "flex", alignItems: "center" } })) : success ? (react_1.default.createElement(foundations_1.Icon, { icon: "check-circle", size: "sm", sx: { display: "flex", alignItems: "center", color: "success01" } })) : (searchIcon);
    }, [error, searchIcon, success]);
    const handleFocus = () => {
        setIsFocused(true);
    };
    const handleBlur = react_1.default.useCallback((event) => {
        if (clearButtonRef.current &&
            event.relatedTarget === clearButtonRef.current &&
            onClickClear) {
            onClickClear();
        }
        handleClickOption(event);
        setIsFocused(false);
        if (onBlur) {
            onBlur(event);
        }
    }, [onBlur, handleClickOption, onClickClear]);
    const labelStyles = react_1.default.useMemo(() => {
        const labelYPosition = shouldShowLabel ? "-24px" : "-50%";
        const labelScale = shouldShowLabel ? 0.66 : 1;
        return {
            position: "absolute",
            left: leftOffset,
            top: "50%",
            transform: `translateY(${labelYPosition}) scale(${labelScale})`,
            transformOrigin: "left center",
            transition: "easeOutFast",
            pointerEvents: "none",
            fontSize: 3,
            lineHeight: 5,
            opacity: value || disabled || readOnly ? 0.5 : 1,
            color: "inputLabel"
        };
    }, [disabled, leftOffset, readOnly, shouldShowLabel, value]);
    const inputStyles = react_1.default.useMemo(() => {
        return Object.assign(Object.assign({ width: "100%", height: size === "small" ? 12 : 14, paddingLeft: leftOffset, paddingTop: shouldShowLabel ? 6 : 4, paddingBottom: shouldShowLabel ? 2 : 4, paddingRight: iconDisplay ? (searchIcon ? 13 : 11) : 4, lineHeight: 5, fontSize: 3, borderRadius: "medium", cursor: disabled || readOnly ? "not-allowed" : "auto", color: "inputLabel", opacity: readOnly ? "0.5" : "1" }, (readOnly && {
            "::selection": {
                backgroundColor: "transparent"
            }
        })), (readOnly && { pointerEvents: "none" }));
    }, [
        size,
        leftOffset,
        shouldShowLabel,
        iconDisplay,
        searchIcon,
        disabled,
        readOnly
    ]);
    const inputBorderStyles = react_1.default.useMemo(() => {
        return Object.assign({ position: "relative", transition: "colorMedium", border: "solid", borderColor: error ? "error01" : isFocused ? "stroke01" : "stroke02", borderWidth: "1px", borderRadius: "inputBorderRadius", marginBottom: error || hint ? 1 : 0, cursor: disabled || readOnly ? "not-allowed" : "auto", backgroundColor: disabled ? "disabled01" : "inputBackgroundColor" }, (!error &&
            !disabled &&
            !readOnly && {
            ":hover": {
                borderColor: "stroke01"
            }
        }));
    }, [disabled, error, readOnly, hint, isFocused]);
    return (react_1.default.createElement(foundations_1.Box, { sx: Object.assign(Object.assign({}, sx), { display: "flex", flexDirection: "column", userSelect: "none" }) },
        react_1.default.createElement(foundations_1.Box, Object.assign({ sx: inputBorderStyles }, ariaPropsForDropdown),
            react_1.default.createElement(foundations_1.Box, { as: "label", htmlFor: inputId, id: `${inputId}-label`, sx: labelStyles }, label),
            react_1.default.createElement(foundations_1.Box, { ref: inputRef, as: "input", id: inputId, type: type, value: value, name: name, onChange: onChange, disabled: disabled, readOnly: readOnly, autoComplete: autocomplete, spellCheck: "false", "aria-disabled": disabled, "aria-invalid": !!error, "aria-readonly": readOnly, "aria-required": required, sx: inputStyles, onBlur: handleBlur, onKeyDown: handleKeyDown, onFocus: handleFocus, onCompositionStart: handleCompositionStart, onCompositionEnd: handleCompositionEnd }),
            onClickClear && value && (react_1.default.createElement(foundations_1.Box, { ref: clearButtonRef, "aria-label": "clear", tabIndex: -1, sx: {
                    position: "absolute",
                    top: "50%",
                    right: iconDisplay ? 15 : 4,
                    transform: "translateY(-50%)",
                    color: "text01",
                    cursor: "pointer"
                }, onClick: onClickClear },
                react_1.default.createElement(foundations_1.Icon, { icon: "x-circle", size: "sm", sx: { display: "flex", alignItems: "center" } }))),
            iconDisplay && (react_1.default.createElement(foundations_1.Box, { ref: iconButtonRef, sx: {
                    position: "absolute",
                    top: "50%",
                    right: 4,
                    transform: "translateY(-50%)",
                    color: "text01"
                } }, iconDisplay)),
            showDropdown && (react_1.default.createElement(OptionDropdown_1.OptionDropdown, { dropdownOptions: dropdownOptions, showNoResults: showNoResults, focusedIdx: focusedOptionIdx, optionDisplayCount: optionDisplayCount, noResultsMessage: noResultsMessage }))),
        error && (react_1.default.createElement(foundations_1.Text, { as: "p", size: "sm", "aria-live": "polite", sx: {
                color: "error01",
                width: "100%"
            } }, error)),
        hint && (react_1.default.createElement(foundations_1.Text, { as: "p", size: "sm", "aria-live": "polite", sx: {
                color: "text02",
                width: "100%"
            } }, hint))));
};
exports.Input = Input;
//# sourceMappingURL=Input.js.map